import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Badge, 
  ProgressBar,
  Table,
  Spinner
} from "react-bootstrap";
import {
  PersonCircle,
  CreditCard,
  Prescription,
  Cash,
  Calendar,
  Clock,
  CheckCircle,
  Shield,
  Heart,
  Activity,
  FileText,
  ChevronRight,
  Eye,
  Award,
  People,
  Download,
  Printer,
} from "react-bootstrap-icons";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";

// Données fictives pour le patient
const mockPatientData = {
  name: "Jean Dupont",
  id: "CNAMGS-123456",
  email: "jean.dupont@email.com",
  coverageRate: 80,
  totalPrescriptions: 12,
  pendingPrescriptions: 3,
  totalReimbursements: 8,
  reimbursedAmount: 125000,
  lastConsultation: "12 mars 2025",
  lastDoctor: "Dr. Nzogho",
  nextAppointment: "25 mars 2025",
  recentCare: [
    { type: "Consultation générale", date: "12 mars 2025", doctor: "Dr. Nzogho" },
    { type: "Analyse sanguine", date: "1 mars 2025", lab: "Laboratoire Central" },
    { type: "Vaccination", date: "15 février 2025", typeVaccin: "Grippe saisonnière" }
  ]
};

// Ordonnances fictives
const mockPrescriptionsList = [
  { id: "ORD-001", date: "2025-03-12", doctorName: "Dr. Nzogho", medicines: [{ name: "Paracétamol", dosage: "500mg", quantity: 10 }], status: "pending" },
  { id: "ORD-002", date: "2025-03-01", doctorName: "Dr. Mba", medicines: [{ name: "Amoxicilline", dosage: "250mg", quantity: 15 }], status: "dispensed" },
  { id: "ORD-003", date: "2025-02-20", doctorName: "Dr. Nzogho", medicines: [{ name: "Ibuprofène", dosage: "400mg", quantity: 20 }], status: "reimbursed" },
  { id: "ORD-004", date: "2025-02-10", doctorName: "Dr. Ondo", medicines: [{ name: "Vitamine C", dosage: "1000mg", quantity: 30 }], status: "dispensed" },
  { id: "ORD-005", date: "2025-01-25", doctorName: "Dr. Mba", medicines: [{ name: "Spasfon", dosage: "80mg", quantity: 12 }], status: "reimbursed" }
];

// Remboursements fictifs
const mockReimbursementsList = [
  { id: "REM-001", date: "2025-03-12", amount: 15000, status: "pending", pharmacy: "Pharmacie Centrale" },
  { id: "REM-002", date: "2025-03-01", amount: 2500, status: "approved", pharmacy: "Pharmacie Moderne" },
  { id: "REM-003", date: "2025-02-20", amount: 8000, status: "approved", pharmacy: "Pharmacie Centrale" },
  { id: "REM-004", date: "2025-02-10", amount: 3500, status: "approved", pharmacy: "Pharmacie du Quartier" },
  { id: "REM-005", date: "2025-01-25", amount: 12000, status: "rejected", pharmacy: "Pharmacie Centrale" }
];

const PatientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [patientData, setPatientData] = useState(mockPatientData);
  const [prescriptions, setPrescriptions] = useState(mockPrescriptionsList);
  const [reimbursements, setReimbursements] = useState(mockReimbursementsList);

  // Calcul des statistiques
  const stats = useMemo(() => ({
    coverageRate: patientData.coverageRate,
    totalPrescriptions: prescriptions.length,
    pendingPrescriptions: prescriptions.filter(p => p.status === 'pending').length,
    totalReimbursements: reimbursements.length,
    reimbursedAmount: reimbursements.filter(r => r.status === 'approved').reduce((sum, r) => sum + r.amount, 0)
  }), [prescriptions, reimbursements, patientData]);

  const recentPrescriptions = prescriptions.slice(0, 5);
  const recentReimbursements = reimbursements.slice(0, 5);

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge bg="warning" className="d-flex align-items-center px-2 py-1"><Clock className="me-1" size={12} /> En attente</Badge>,
      dispensed: <Badge bg="info" className="d-flex align-items-center px-2 py-1"><CheckCircle className="me-1" size={12} /> Délivrée</Badge>,
      reimbursed: <Badge bg="success" className="d-flex align-items-center px-2 py-1"><CheckCircle className="me-1" size={12} /> Remboursée</Badge>,
      approved: <Badge bg="success" className="d-flex align-items-center px-2 py-1"><CheckCircle className="me-1" size={12} /> Approuvé</Badge>,
      rejected: <Badge bg="danger" className="d-flex align-items-center px-2 py-1">Rejeté</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  // Cartes de statistiques avec hauteur égale
  const statsCards = useMemo(() => [
    {
      title: "Total Ordonnances",
      value: stats.totalPrescriptions,
      icon: Prescription,
      color: "primary",
      badge: { text: "Prescriptions", icon: <FileText className="me-1" size={12} /> },
      progress: { value: stats.totalPrescriptions > 0 ? 100 : 0, label: "Taux d'activité" }
    },
    {
      title: "En attente",
      value: stats.pendingPrescriptions,
      icon: Clock,
      color: "warning",
      badge: { text: "En attente", icon: <Clock className="me-1" size={12} /> },
      progress: { value: stats.totalPrescriptions > 0 ? (stats.pendingPrescriptions / stats.totalPrescriptions) * 100 : 0, label: "Proportion" }
    },
    {
      title: "Remboursements",
      value: formatCurrency(stats.reimbursedAmount),
      icon: Cash,
      color: "success",
      badge: { text: `${stats.totalReimbursements} demande(s)`, icon: <Cash className="me-1" size={12} /> },
      progress: { value: stats.reimbursedAmount > 0 ? 75 : 0, label: "Progression" }
    },
    {
      title: "Couverture",
      value: `${stats.coverageRate}%`,
      icon: Shield,
      color: "info",
      badge: { text: "Soins courants", icon: <Shield className="me-1" size={12} /> },
      progress: { value: stats.coverageRate, label: "Taux de couverture" }
    }
  ], [stats]);

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">






        {/* En-tête avec breadcrumb */}
      




        <div className="welcome-section mb-4 p-4 rounded-4" style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
        }}>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-circle p-3">
                  <PersonCircle size={48} className="text-primary" />
                </div>
                <div>
                  <h1 className="h3 fw-bold mb-1">
                    Bonjour, {user?.name?.split(' ')[0] || 'Dr. Nzogho'} !
                  </h1>
                  <p className="mb-0 opacity-90">
                    Voici le résumé de votre activité médicale aujourd'hui.
                  </p>
                  <div className="mt-2">
                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                      <Activity className="me-1" size={14} />
                      2 ordonnances aujourd'hui
                    </Badge>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button variant="light" className="rounded-pill px-4 me-2">
                <Download size={16} className="me-2" /> Exporter
              </Button>
              <Button variant="outline-light" className="rounded-pill px-4">
                <Printer size={16} className="me-2" /> Imprimer
              </Button>
            </Col>
          </Row>
        </div>

      <Row className="g-2 mb-4">
  {statsCards.map((card, idx) => (
    <Col md={6} lg={3} xs={6} key={idx}>
      <Card className="border-0 shadow-sm overflow-hidden stats-card h-100 bg-white">
        <div className={`card-gradient bg-${card.color}`} style={{ height: '3px' }}></div>
        <Card.Body className="p-3 d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="text-uppercase text-muted mb-1 small fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                {card.title}
              </h6>
              <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                {card.value}
              </h3>
              <div className="mt-1">
                <Badge bg={`${card.color}-subtle`} text={card.color} className="fw-medium d-inline-flex align-items-center" style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem' }}>
                  {card.badge.icon}
                  {card.badge.text}
                </Badge>
              </div>
            </div>
          </div>
          <div className="progress-wrapper mt-auto">
            <div className="d-flex justify-content-between mb-0">
              <small className="text-muted" style={{ fontSize: '0.7rem' }}>{card.progress.label}</small>
              <small className="fw-medium" style={{ fontSize: '0.7rem' }}>{Math.round(card.progress.value)}%</small>
            </div>
            <ProgressBar 
              now={card.progress.value} 
              variant={card.color}
              className="stats-progress"
              style={{ height: '4px' }}
            />
          </div>
        </Card.Body>
      </Card>
    </Col>
  ))}
</Row>

        {/* Section des ordonnances récentes */}
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <Prescription className="me-2" size={18} />
                  <span className="fw-semibold">Ordonnances récentes</span>
                </div>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={() => navigate('/patient/prescriptions')}
                >
                  Voir tout <ChevronRight size={14} />
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 table-striped">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Médecin</th>
                        <th>Médicaments</th>
                        <th>Statut</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentPrescriptions.length > 0 ? (
                        recentPrescriptions.map(p => (
                          <tr key={p.id}>
                            <td className="small">{formatDate(p.date)}</td>
                            <td className="fw-medium">{p.doctorName}</td>
                            <td className="small text-muted">
                              {p.medicines.slice(0, 2).map(m => m.name).join(', ')}
                              {p.medicines.length > 2 && ` +${p.medicines.length - 2}`}
                            </td>
                            <td>{getStatusBadge(p.status)}</td>
                            <td>
                              <Button 
                                size="sm" 
                                variant="outline-primary"
                                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => navigate('/patient/prescriptions')}
                              >
                                <Eye size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <Prescription size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune ordonnance trouvée</p>
                            <Button variant="primary" size="sm" onClick={() => navigate('/doctor/search')}>
                              Consulter un médecin
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <Cash className="me-2" size={18} />
                  <span className="fw-semibold">Remboursements récents</span>
                </div>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={() => navigate('/patient/remboursements')}
                >
                  Voir tout <ChevronRight size={14} />
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 table-striped">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentReimbursements.length > 0 ? (
                        recentReimbursements.map(r => (
                          <tr key={r.id}>
                            <td className="small">{formatDate(r.date)}</td>
                            <td className="fw-bold text-success">{formatCurrency(r.amount)}</td>
                            <td>{getStatusBadge(r.status)}</td>
                            <td>
                              <Button 
                                size="sm" 
                                variant="outline-success"
                                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => navigate('/patient/remboursements')}
                              >
                                <Eye size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-5">
                            <Cash size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucun remboursement trouvé</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Informations personnelles */}
        <Row className="g-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm overflow-hidden h-100">
              <div className="card-gradient bg-info"></div>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <h5 className="mb-3 fw-semibold d-flex align-items-center">
                      <Activity className="me-2 text-info" size={20} />
                      Mes informations
                    </h5>
                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span className="text-muted">Nom complet</span>
                        <strong>{patientData.name}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span className="text-muted">Numéro d'assuré</span>
                        <strong>{patientData.id}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span className="text-muted">Email</span>
                        <strong>{patientData.email}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span className="text-muted">Dernière consultation</span>
                        <strong>{patientData.lastConsultation}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Taux de couverture</span>
                        <Badge bg="primary" className="rounded-pill px-3 py-2">{stats.coverageRate}%</Badge>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h5 className="mb-3 fw-semibold d-flex align-items-center">
                      <Heart className="me-2 text-danger" size={20} />
                      Derniers soins
                    </h5>
                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span>Consultation générale</span>
                        <span className="text-muted small">12 mars 2025</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span>Analyse sanguine</span>
                        <span className="text-muted small">1 mars 2025</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span>Vaccination</span>
                        <span className="text-muted small">15 février 2025</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center pt-2">
                        <span className="fw-semibold">Prochain rendez-vous</span>
                        <span className="text-primary fw-bold">{patientData.nextAppointment}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
<br></br><br></br>
        {/* Bannière d'action rapide */}
        {/* <Row className="mt-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm overflow-hidden bg-primary text-white">
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="mb-2 fw-bold">Besoin d'une consultation médicale ?</h5>
                    <p className="mb-0 opacity-90 small">
                      Prenez rendez-vous avec un médecin partenaire CNAMGS en quelques clics
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <Button 
                      variant="light" 
                      className="rounded-pill px-4 py-2 fw-semibold"
                      onClick={() => navigate('/doctor/search')}
                    >
                      Prendre rendez-vous
                      <ChevronRight className="ms-2" size={16} />
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row> */}

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 12px;
            overflow: hidden;
          }

          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
          }

          .card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }

          .icon-container {
            width: 56px;
            height: 56px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .stats-progress {
            height: 6px;
            border-radius: 3px;
          }

          .stats-progress .progress-bar {
            border-radius: 3px;
          }

          @media (max-width: 768px) {
            .stats-card .icon-container {
              width: 48px;
              height: 48px;
            }
            
            .stats-card h2 {
              font-size: 1.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PatientDashboard;